import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, CircularProgress,
  Card, CardContent, Grid, Stack,
} from '@mui/material';
import {
  AccountBalanceWallet as SalaryIcon,
  TrendingUp as EarningsIcon,
  TrendingDown as DeductionsIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import {
  SalaryStructureDto,
  SalaryComponentType,
} from '../../../types/payrollTypes';

const earningComponentTypes = [
  SalaryComponentType.Earning,
  SalaryComponentType.Allowance,
  SalaryComponentType.ReimbursableComponent,
  SalaryComponentType.BaseComponent,
  SalaryComponentType.VariablePay,
  SalaryComponentType.Overtime,
];

const deductionComponentTypes = [
  SalaryComponentType.Deduction,
  SalaryComponentType.TaxComponent,
  SalaryComponentType.LeaveComponent,
];

const MySalaryPage: React.FC = () => {
  const [structure, setStructure] = useState<SalaryStructureDto | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await payrollApi.getMySalary();
        setStructure(data);
      } catch {
        toast.error('Failed to load salary details');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!structure) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" fontWeight={700} mb={3}>My Salary</Typography>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No salary structure found. Please contact HR.</Typography>
        </Paper>
      </Box>
    );
  }

  const earnings = structure.components.filter(c => earningComponentTypes.includes(c.componentType));
  const deductions = structure.components.filter(c => deductionComponentTypes.includes(c.componentType));
  const totalEarnings = earnings.reduce((sum, c) => sum + c.amount, 0);
  const totalDeductions = deductions.reduce((sum, c) => sum + c.amount, 0);
  const netSalary = totalEarnings - totalDeductions;

  const summaryCards = [
    { label: 'CTC (Annual)', value: `₹${structure.ctc.toLocaleString('en-IN')}`, icon: <SalaryIcon />, color: '#667eea' },
    { label: 'Monthly Earnings', value: `₹${totalEarnings.toLocaleString('en-IN')}`, icon: <EarningsIcon />, color: '#66bb6a' },
    { label: 'Monthly Deductions', value: `₹${totalDeductions.toLocaleString('en-IN')}`, icon: <DeductionsIcon />, color: '#ef5350' },
    { label: 'Net Salary', value: `₹${netSalary.toLocaleString('en-IN')}`, icon: <SalaryIcon />, color: '#29b6f6' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>My Salary</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Effective from: {new Date(structure.effectiveFrom).toLocaleDateString('en-IN')}
      </Typography>

      <Grid container spacing={2} mb={4}>
        {summaryCards.map((card) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={card.label}>
            <Card sx={{ borderLeft: `4px solid ${card.color}` }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <Box sx={{ color: card.color }}>{card.icon}</Box>
                  <Typography variant="body2" color="text.secondary">{card.label}</Typography>
                </Stack>
                <Typography variant="h5" fontWeight={700}>{card.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2} color="success.main">Earnings</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Monthly Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {earnings.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.componentName}</TableCell>
                      <TableCell align="right">₹{c.amount.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total Earnings</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>₹{totalEarnings.toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={600} mb={2} color="error.main">Deductions</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell align="right">Monthly Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deductions.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{c.componentName}</TableCell>
                      <TableCell align="right">₹{c.amount.toLocaleString('en-IN')}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Total Deductions</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>₹{totalDeductions.toLocaleString('en-IN')}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MySalaryPage;
